import { useMemo, useState } from 'react';
import {
  createNoShow,
  useClients,
  useLatestClientPhotoUrls,
  type ClientRow,
} from '@busy/api';
import { Avatar, ModalCloseButton, ModalShell, useToast } from '@busy/ui';
import { useT } from '../../lib/i18n';

// "Mark no-show" picker — opens from TodaysChair when the barber taps
// the no-show secondary button. Reuses the same client-list pattern
// the cut flow uses but persists to no_shows (migration 0076) instead
// of visits.
//
// UX: minimal — search + scroll. Tapping a client logs the no-show
// and closes the modal with a toast. No edit screen for notes in v1;
// the column exists for v1.1 when the flight-risk coaching surface
// wants to capture "no contact" vs "texted late" detail.

export function NoShowPickerModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const clients = useClients();
  const photoUrls = useLatestClientPhotoUrls();
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  // No-show writes are permanent — there's no UI to remove an entry
  // once it's logged (the table is append-only and the metric folds
  // straight into flight-risk scoring). Stage the target client here
  // and require an explicit second tap before calling createNoShow.
  const [pendingClient, setPendingClient] = useState<ClientRow | null>(null);

  // Filter clients by query — case-insensitive, first/last/phone.
  // Excludes Anonymous-Client placeholder rows since logging a no-show
  // against "Anonymous Client0042" defeats the purpose of the metric.
  const filtered = useMemo(() => {
    const all = (clients.data ?? []).filter(
      (c) => c.first_name !== 'Anonymous' && !c.archived_at,
    );
    if (!query.trim()) return all;
    const q = query.trim().toLowerCase();
    return all.filter((c) => {
      const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.toLowerCase();
      const phone = (c.phone ?? '').toLowerCase();
      return name.includes(q) || phone.includes(q);
    });
  }, [clients.data, query]);

  // Tap from the list — stage the client and open the confirm step.
  // We don't write to the DB until the barber confirms in the second
  // modal (no-shows can't be undone, so a single tap is too easy to
  // misfire on a phone in a busy chair).
  function handlePick(client: ClientRow) {
    if (saving) return;
    setPendingClient(client);
  }

  async function handleConfirm() {
    if (!pendingClient) return;
    setSaving(true);
    const { error } = await createNoShow({ client_id: pendingClient.id });
    setSaving(false);
    if (error) {
      toast(t('cut.noShow.error', { message: error.message }), 'error');
      // Leave the confirm open so the barber can retry or back out.
      return;
    }
    const name = `${pendingClient.first_name} ${pendingClient.last_name}`.trim()
      || t('today.unnamed');
    toast(t('cut.noShow.logged', { name }), 'success');
    setPendingClient(null);
    onClose();
  }

  return (
    <ModalShell open onClose={onClose} size="md" ariaLabel={t('cut.noShow.aria')}>
      <ModalCloseButton onClose={onClose} />
      <div className="border-b border-line p-4">
        <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-amber-700 dark:text-amber-400 inline-flex items-center gap-1.5 mb-1.5">
          <CalendarMissedIcon className="w-3 h-3" />
          {t('cut.noShow.eyebrow')}
        </div>
        <h2 className="font-display text-xl leading-tight">{t('cut.noShow.title')}</h2>
        <p className="text-[12px] text-ink-500 mt-1.5 leading-snug">
          {t('cut.noShow.body')}
        </p>
      </div>

      <div className="p-4 space-y-3">
        <input
          type="search"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('cut.noShow.searchPlaceholder')}
          className="w-full h-11 px-3 rounded-lg border border-line bg-surface text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
        />

        {clients.loading ? (
          <ul className="space-y-2">
            {[0, 1, 2].map((i) => (
              <li key={i} className="h-14 rounded-lg bg-bg-alt animate-pulse" />
            ))}
          </ul>
        ) : filtered.length === 0 ? (
          <p className="text-center text-sm text-ink-500 py-6">
            {query.trim() ? t('cut.noShow.noMatches') : t('cut.noShow.emptyRoster')}
          </p>
        ) : (
          <ul className="space-y-1.5 max-h-[50dvh] overflow-y-auto -mx-1 px-1">
            {filtered.slice(0, 40).map((c) => {
              const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()
                || t('today.unnamed');
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => handlePick(c)}
                    className="w-full text-left flex items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2.5 hover:bg-bg-alt transition-colors disabled:opacity-50"
                  >
                    <Avatar
                      name={name}
                      size="sm"
                      photoUrl={photoUrls.data?.[c.id]}
                    />
                    <div className="flex-1 min-w-0">
                      <strong className="block truncate">{name}</strong>
                      {c.phone && (
                        <div className="text-[11px] text-ink-500 tabular-nums truncate">
                          {c.phone}
                        </div>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {pendingClient && (
        <NoShowConfirmModal
          client={pendingClient}
          saving={saving}
          onConfirm={handleConfirm}
          onCancel={() => setPendingClient(null)}
        />
      )}
    </ModalShell>
  );
}

// Nested confirm — the no-show write is permanent, so we make the
// barber commit twice. Picker stays mounted underneath; backing out
// returns them to the search list (rather than collapsing the whole
// flow, which would punish a stray tap).
function NoShowConfirmModal({
  client,
  saving,
  onConfirm,
  onCancel,
}: {
  client: ClientRow;
  saving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  const name = `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim()
    || t('today.unnamed');
  return (
    <ModalShell open onClose={onCancel} size="sm" ariaLabel={t('cut.noShow.confirmAria')}>
      <div className="p-5 space-y-3">
        <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-amber-700 dark:text-amber-400">
          {t('cut.noShow.confirmEyebrow')}
        </div>
        <h3 className="font-display text-xl leading-tight">
          {t('cut.noShow.confirmTitle', { name })}
        </h3>
        <p className="text-sm text-ink-700 dark:text-ink-300 leading-relaxed">
          {t('cut.noShow.confirmBody')}
        </p>
        <div className="flex flex-col gap-2 pt-2">
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            className="w-full h-11 rounded-lg bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:opacity-50"
          >
            {t('cut.noShow.confirmCta')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="w-full h-11 rounded-lg border border-line bg-surface font-semibold disabled:opacity-50"
          >
            {t('cut.noShow.confirmCancel')}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/** Calendar-with-X icon — signals "missed slot" visually. SVG only
 *  (no emoji per house rule). */
function CalendarMissedIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <line x1="9" y1="15" x2="15" y2="15" />
    </svg>
  );
}
